/**
 * Animation for the ops protocol (docs/ops-protocol.md, "Phase 3 additions").
 *
 * The renderer records what a render pass did (which elements were updated,
 * what their styles were before, which were inserted, which were removed and
 * where they were) and hands the record to the `Animator` at `commit`. When
 * the commit carries an `animation` (`withAnimation`) or an element's
 * `animationToken` changed in an `update` (`.animation(_:value:)`), the
 * Animator animates the changes of that pass with the Web Animations API:
 *
 * - style changes (opacity, colors, border radius, font size, transform and
 *   filter; frames are tweened from the layout engine's boxes) tween from
 *   the recorded value to the new one;
 * - inserted elements play their `transition` forwards;
 * - removed elements leave a visual clone (stripped of element ids) at their
 *   old position which plays the `transition` backwards and is then dropped.
 *
 * A frame tween is retargeted when a later layout pass moves the element
 * again before it finished: the running tween is replaced by one from where
 * the element is drawn now to its new frame, in the new commit's animation or,
 * for a pass that is not animated (a custom `Layout` or `GeometryReader`
 * answering the animated pass), in the time the old tween had left. Without
 * that the old tween, which overrides the inline frame, kept drawing the
 * element toward the stale frame until it ended and then snapped.
 *
 * `Animation` maps to CSS easings; `spring` becomes a `linear()` easing
 * sampled from a damped spring where supported.
 *
 * iOS 26's bar toolbars animate on their own: when an item comes or goes,
 * or an item resizes, the glass capsule grows or shrinks with a spring and
 * the items slide, the new one scaling and fading in, the old one out, in a
 * pass nothing else animates (`implicitToolbars`). The commit's animation,
 * or an `.animation` on the toolbar, wins when there is one.
 */

import type { Animation, Edge, Transition, TransitionPart } from './protocol';
import { ghostClone } from './ghost';

/**
 * Style properties tweened between the value before the pass and after.
 * Geometry (left/top/width/height) is animated from the layout engine's
 * frames instead (see `FrameChange`).
 */
export const ANIMATED_PROPERTIES = [
  'opacity',
  'backgroundColor',
  'color',
  'borderRadius',
  'fontSize',
  // Phase 6 visual effects (offset / rotation / scale, shadow).
  'transform',
  'filter',
] as const;
export type AnimatedProperty = (typeof ANIMATED_PROPERTIES)[number];

export type StyleSnapshot = Partial<Record<AnimatedProperty, string>>;

export interface UpdatedRecord {
  el: HTMLElement;
  /** Computed values before the update (null when the element was not rendered). */
  before: StyleSnapshot | null;
  /** `animationToken` changed in this pass; `animation` is the subtree's. */
  tokenChanged: boolean;
  animation?: Animation;
  /**
   * The change animates by default (an inspector shown or hidden): with the
   * commit's animation when it has one, else with `default`.
   */
  implicit?: boolean;
}

export interface RemovedRecord {
  el: HTMLElement;
  /** Ancestors (nearest first) at the time of removal, for token lookup. */
  ancestors: HTMLElement[];
  /** Position relative to the animation root, before removal. */
  rect: DOMRect | null;
  transition: Transition | undefined;
  /** `.matchedGeometryEffect` boxes in the removed subtree (itself included): their ids and where they were. */
  matched?: { id: string; el: HTMLElement; rect: DOMRect }[];
}

/** A text whose content changed under a `contentTransition`: a copy of how it looked, and where. */
export interface ContentChange {
  el: HTMLElement;
  clone: HTMLElement;
  rect: DOMRect;
  kind: 'numericText' | 'opacity' | 'interpolate';
  countsDown: boolean;
}

export interface PassRecord {
  updated: Map<HTMLElement, UpdatedRecord>;
  /** Elements attached by `insert` in this pass that were detached before. */
  inserted: Set<HTMLElement>;
  removed: RemovedRecord[];
  contentChanges: ContentChange[];
}

export function emptyPass(): PassRecord {
  return { updated: new Map(), inserted: new Set(), removed: [], contentChanges: [] };
}

export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** An element whose layout box changed in the pass (`from` is null for a new element). */
export interface FrameChange {
  el: HTMLElement;
  kind: string;
  from: FrameRect | null;
  to: FrameRect;
}

/** A screen below the top of its navigation stack (the renderer marks it). */
const BELOW_SCREEN = '.sb-navscreen[data-sb-nav-position="below"]';
/** A push or pop in progress in a stack: its top screen sliding in, or a popped screen's clone sliding out. */
const NAV_IN_FLIGHT = ':scope > .sb-slot > .sb-navscreen--push, :scope > .sb-navscreen--pop';

export const DEFAULT_DURATION = 0.35;
export const EASE_IN_OUT = 'cubic-bezier(0.42, 0, 0.58, 1)';
export const EASE_IN = 'cubic-bezier(0.42, 0, 1, 1)';
export const EASE_OUT = 'cubic-bezier(0, 0, 0.58, 1)';
/** Samples of the spring curve in a `linear()` easing. */
export const SPRING_SAMPLES = 60;

const supportsLinearEasing: boolean =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('animation-timing-function', 'linear(0, 0.5, 1)')
    : false;

export interface Timing {
  /** Milliseconds. */
  duration: number;
  delay: number;
  easing: string;
  /**
   * Phase 8 `Animation.repeat`: how many times the tween plays (Infinity for
   * `forever`); absent for a plain animation (WAAPI's default of 1).
   */
  iterations?: number;
  /** Phase 8: `alternate` when the repeat autoreverses; absent otherwise. */
  direction?: PlaybackDirection;
}

/**
 * CSS timing for a protocol `Animation`. The `repeat` fields apply to the
 * style and frame tweens of a commit (`Animator.animateChanges` /
 * `animateFrame`); transitions play once, see `baseTiming`.
 */
export function timingFor(animation: Animation | undefined): Timing {
  const a = animation ?? { type: 'default' };
  const duration = Math.max(0, (a.duration ?? DEFAULT_DURATION) * 1000);
  const delay = Math.max(0, (a.delay ?? 0) * 1000);
  const timing: Timing = { duration, delay, easing: easingFor(a) };
  const repeat = a.repeat;
  if (repeat && typeof repeat === 'object') {
    if (repeat.count === 'forever') timing.iterations = Infinity;
    else if (typeof repeat.count === 'number' && Number.isFinite(repeat.count) && repeat.count > 0) {
      timing.iterations = repeat.count;
    }
    if (repeat.autoreverses === true && timing.iterations !== undefined) timing.direction = 'alternate';
  }
  return timing;
}

/** `timing` without its repeat: enter / exit transitions play once. */
export function baseTiming(timing: Timing): Timing {
  return { duration: timing.duration, delay: timing.delay, easing: timing.easing };
}

/** CSS `animation-timing-function` for a protocol `Animation`. */
export function easingFor(animation: Animation | undefined): string {
  switch (animation?.type) {
    case 'linear':
      return 'linear';
    case 'easeIn':
      return EASE_IN;
    case 'easeOut':
      return EASE_OUT;
    case 'spring':
      return supportsLinearEasing
        ? springEasing(animation.duration ?? 0.5, animation.bounce ?? 0)
        : EASE_IN_OUT;
    case 'default':
    case 'easeInOut':
    default:
      return EASE_IN_OUT;
  }
}

/**
 * `linear()` easing of a damped spring with SwiftUI's `Spring(duration:bounce:)`
 * parameterization: natural frequency ω = 2π / duration, damping ratio
 * ζ = 1 − bounce (so bounce 0 is critically damped and 0.3 overshoots). The
 * curve is sampled at `SPRING_SAMPLES` points over the perceptual duration,
 * by which time the spring has settled to within ~1%.
 */
export function springEasing(duration: number, bounce: number, samples = SPRING_SAMPLES): string {
  // An animated pass asks for the same few curves once per tweened element.
  const key = `${duration} ${bounce} ${samples}`;
  let easing = springEasings.get(key);
  if (easing === undefined) {
    if (springEasings.size >= MAX_SPRING_EASINGS) springEasings.clear();
    easing = sampleSpring(duration, bounce, samples);
    springEasings.set(key, easing);
  }
  return easing;
}

const springEasings = new Map<string, string>();
const MAX_SPRING_EASINGS = 64;

function sampleSpring(duration: number, bounce: number, samples: number): string {
  const d = duration > 0 ? duration : 0.5;
  const zeta = Math.min(1, Math.max(0, 1 - bounce));
  const omega = (2 * Math.PI) / d;
  const points: string[] = [];
  for (let i = 0; i < samples; i++) {
    const t = (i / (samples - 1)) * d;
    points.push(springPosition(t, omega, zeta).toFixed(4));
  }
  points[0] = '0';
  points[samples - 1] = '1';
  return `linear(${points.join(', ')})`;
}

/**
 * The eased progress of `animation` `elapsed` milliseconds after it began
 * (its delay included): 0 before it starts, its curve's value while it runs
 * (a spring that bounces passes 1), 1 after. For what CSS cannot tween, such
 * as a custom shape's path (src/pathShape.ts), in the curve its CSS
 * counterpart (`easingFor`) uses. Repeats are not applied.
 */
export function easedProgress(animation: Animation | undefined, elapsed: number): number {
  const timing = timingFor(animation);
  if (elapsed <= timing.delay) return 0;
  if (timing.duration <= 0 || elapsed >= timing.delay + timing.duration) return 1;
  const t = (elapsed - timing.delay) / timing.duration;
  switch (animation?.type) {
    case 'linear':
      return t;
    case 'easeIn':
      return cubicBezier(0.42, 0, 1, 1, t);
    case 'easeOut':
      return cubicBezier(0, 0, 0.58, 1, t);
    case 'spring': {
      const d = animation.duration ?? 0.5;
      const zeta = Math.min(1, Math.max(0, 1 - (animation.bounce ?? 0)));
      return springPosition(t * (d > 0 ? d : 0.5), (2 * Math.PI) / (d > 0 ? d : 0.5), zeta);
    }
    default:
      return cubicBezier(0.42, 0, 0.58, 1, t);
  }
}

/** The y of the CSS cubic Bézier through (0,0), (x1,y1), (x2,y2), (1,1) at x = `t`. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number, t: number): number {
  const at = (a: number, b: number, s: number) => 3 * a * s * (1 - s) ** 2 + 3 * b * s * s * (1 - s) + s ** 3;
  // Bisection on x (monotonic for CSS's curves).
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (at(x1, x2, mid) < t) lo = mid;
    else hi = mid;
  }
  return at(y1, y2, (lo + hi) / 2);
}

function springPosition(t: number, omega: number, zeta: number): number {
  if (zeta >= 1) {
    // Critically damped.
    return 1 - Math.exp(-omega * t) * (1 + omega * t);
  }
  const omegaD = omega * Math.sqrt(1 - zeta * zeta);
  const envelope = Math.exp(-zeta * omega * t);
  return 1 - envelope * (Math.cos(omegaD * t) + ((zeta * omega) / omegaD) * Math.sin(omegaD * t));
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

/** SwiftUI's default transition for views inserted or removed with animation. */
export const DEFAULT_TRANSITION: Transition = { kind: 'opacity' };

/** The spring a bar toolbar's groups change with (iOS 26), and how its items come and go. */
export const TOOLBAR_ANIMATION: Animation = { type: 'spring', duration: 0.5, bounce: 0.2 };
export const TOOLBAR_TRANSITION: Transition = { kind: 'scale', scale: 0.6 };
/** The `kind` of a bar glass capsule's frame change (src/layoutDom.ts): chrome, not an element. */
export const BAR_GLASS_KIND = 'barglass';

const BAR_PLACEMENTS: ReadonlySet<string> = new Set(['leading', 'trailing', 'principal', 'bottom']);

/** Whether `el` is a toolbar group in a navigation bar or bottom bar (not a `ToolbarSpacer`). */
export function isBarToolbar(el: Element | null | undefined): el is HTMLElement {
  return (
    el instanceof HTMLElement &&
    el.classList.contains('sb-toolbar') &&
    !el.hasAttribute('data-sb-spacer') &&
    BAR_PLACEMENTS.has(el.dataset['sbPlacement'] ?? '')
  );
}

/** The bar toolbar `el` is, or is an item of (`parent`: where a removed item was). */
export function barToolbarOf(el: HTMLElement, parent: Element | null = el.parentElement): HTMLElement | null {
  if (isBarToolbar(el)) return el;
  return isBarToolbar(parent) ? parent : null;
}

/** The transition a view without one of its own enters and leaves with: a bar item's scales. */
function implicitTransitionOf(el: HTMLElement, parent: Element | null = el.parentElement): Transition | undefined {
  return barToolbarOf(el, parent) ? TOOLBAR_TRANSITION : undefined;
}

/**
 * The bar toolbars whose groups this pass changed, each with its screen: one
 * that gained or lost an item (or came or went itself, on a screen already
 * there), or whose capsule or an item of it resized; and a group that only
 * moved because another of its screen's changed, which slides over with
 * it, as its capsule does. A group that only moved on its own (the screen
 * resized, a More button appeared beside it) is not one: its items are as
 * they were.
 */
export function implicitToolbars(pass: PassRecord, frames: readonly FrameChange[]): Map<HTMLElement, Element | null> {
  const out = new Map<HTMLElement, Element | null>();
  const moved = new Map<HTMLElement, Element | null>();
  const isNew = (toolbar: HTMLElement): boolean => pass.inserted.has(toolbar) || hasAncestorIn(toolbar, pass.inserted);
  for (const change of frames) {
    const toolbar = barToolbarOf(change.el);
    if (!toolbar || !change.from || !change.el.isConnected || isNew(toolbar)) continue;
    const { from, to } = change;
    if (change.el === toolbar && from.width === to.width && from.height === to.height) {
      moved.set(toolbar, toolbar.closest('.sb-navscreen'));
      continue;
    }
    out.set(toolbar, toolbar.closest('.sb-navscreen'));
  }
  for (const el of pass.inserted) {
    const toolbar = barToolbarOf(el);
    if (!toolbar || !el.isConnected || hasAncestorIn(toolbar, pass.inserted)) continue;
    // A whole new group enters as one; an item of a group already there is the group's change.
    if (el !== toolbar && pass.inserted.has(toolbar)) continue;
    out.set(toolbar, toolbar.closest('.sb-navscreen'));
  }
  for (const rec of pass.removed) {
    const toolbar = barToolbarOf(rec.el, rec.ancestors[0] ?? null);
    if (!toolbar) continue;
    // A removed group is detached: its screen is among the ancestors it had.
    out.set(toolbar, toolbar.isConnected ? toolbar.closest('.sb-navscreen') : (rec.ancestors.find((a) => a.classList.contains('sb-navscreen')) ?? null));
  }
  // (a group the bar does not draw, `secondary` items collapsed into the More button, moves nothing beside it)
  const changedScreens = new Set(Array.from(out).filter(([toolbar]) => !toolbar.hidden).map(([, screen]) => screen));
  for (const [toolbar, screen] of moved) {
    if (!out.has(toolbar) && changedScreens.has(screen)) out.set(toolbar, screen);
  }
  return out;
}

interface TransitionFrame {
  opacity?: number;
  transform?: string;
}

/** Keyframe of the "gone" state of a transition (enter start / exit end). */
export function transitionStart(transition: Transition | undefined, exit: boolean): TransitionFrame {
  const acc: { opacity?: number; transforms: string[] } = { transforms: [] };
  collectTransition(transition ?? DEFAULT_TRANSITION, exit, acc, 0);
  const frame: TransitionFrame = {};
  if (acc.opacity !== undefined) frame.opacity = acc.opacity;
  if (acc.transforms.length) frame.transform = acc.transforms.join(' ');
  return frame;
}

/** Nesting limit for `asymmetric` parts (they may nest; a malformed cycle must not recurse forever). */
const MAX_TRANSITION_DEPTH = 8;

function collectTransition(
  transition: Transition,
  exit: boolean,
  acc: { opacity?: number; transforms: string[] },
  depth: number,
): void {
  if (depth > MAX_TRANSITION_DEPTH) return;
  const parts: TransitionPart[] = Array.isArray(transition) ? transition : [transition];
  for (const part of parts) {
    if (!part || typeof part !== 'object') continue;
    switch (part.kind) {
      case 'opacity':
        acc.opacity = 0;
        break;
      case 'scale':
        acc.opacity = 0;
        acc.transforms.push(`scale(${part.scale ?? 0.5})`);
        break;
      case 'slide':
        // In from the leading edge, out through the trailing edge.
        acc.transforms.push(edgeTranslation(exit ? 'trailing' : 'leading'));
        break;
      case 'move':
        acc.transforms.push(edgeTranslation(part.edge ?? 'bottom'));
        break;
      case 'offset':
        acc.transforms.push(`translate(${part.x ?? 0}px, ${part.y ?? 0}px)`);
        break;
      case 'asymmetric': {
        // Phase 6: `.asymmetric(insertion:removal:)` plays one transition
        // when the view appears and another when it disappears.
        const chosen = exit ? part.removal : part.insertion;
        if (chosen) collectTransition(chosen, exit, acc, depth + 1);
        break;
      }
      case 'identity':
      default:
        break;
    }
  }
}

function edgeTranslation(edge: Edge): string {
  switch (edge) {
    case 'top':
      return 'translateY(-100%)';
    case 'bottom':
      return 'translateY(100%)';
    case 'leading':
      return 'translateX(-100%)';
    case 'trailing':
    default:
      return 'translateX(100%)';
  }
}

function isIdentity(frame: TransitionFrame): boolean {
  return frame.opacity === undefined && frame.transform === undefined;
}

// ---------------------------------------------------------------------------
// Animator
// ---------------------------------------------------------------------------

export const EXIT_CLONE_CLASS = 'sb-exit-clone';

export interface AnimatorOptions {
  /** Kinds (by `data-sb-kind`) that animate their own enter/exit (navigation, sheets). */
  selfAnimatingKinds?: ReadonlySet<string>;
  /**
   * Plays what an updated element animates itself (a custom shape's path
   * frames, src/pathShape.ts) in the animation its update has; returns
   * whether it had anything to play.
   */
  playOwnFrames?: (el: HTMLElement, animation: Animation | undefined) => boolean;
}

export class Animator {
  /** The element clones of removed elements are appended to (the screen), unless they were in a sheet: `placeClone`. */
  private readonly root: HTMLElement;
  private readonly selfAnimating: ReadonlySet<string>;
  private readonly playOwnFrames: AnimatorOptions['playOwnFrames'];

  constructor(root: HTMLElement, options: AnimatorOptions = {}) {
    this.root = root;
    this.selfAnimating = options.selfAnimatingKinds ?? new Set();
    this.playOwnFrames = options.playOwnFrames;
  }

  /** Commits waiting for the next animation frame (`commitNextFrame`), in order. */
  private pending: { pass: PassRecord; animation: Animation | undefined; frames: readonly FrameChange[]; onCommitted?: ((started: globalThis.Animation[]) => void) | undefined }[] = [];
  private pendingFrame: number | null = null;

  /**
   * `commit` in the next animation frame's callback, before the frame
   * paints. A commit reads what the pass just wrote (the updated elements'
   * computed styles, the removed boxes' places): read in the pass, they
   * make the browser lay its frames out once more; read in the frame's
   * callback, they cost the frame its one layout and the tweens still start
   * on it. In a hidden page nothing paints, so the commit runs now.
   */
  commitNextFrame(pass: PassRecord, animation: Animation | undefined, frames: readonly FrameChange[] = [], onCommitted?: (started: globalThis.Animation[]) => void): void {
    if (typeof requestAnimationFrame !== 'function' || (typeof document !== 'undefined' && document.hidden)) {
      this.flushCommits();
      const started = this.commit(pass, animation, frames, onCommitted !== undefined);
      onCommitted?.(started);
      return;
    }
    this.pending.push({ pass, animation, frames, onCommitted });
    this.pendingFrame ??= requestAnimationFrame(() => this.flushCommits());
  }

  /** Run the commits waiting for the next frame now, in order. */
  flushCommits(): void {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
    const pending = this.pending;
    this.pending = [];
    for (const { pass, animation, frames, onCommitted } of pending) {
      const started = this.commit(pass, animation, frames, onCommitted !== undefined);
      onCommitted?.(started);
    }
  }

  /** Every animation running under the root (the exit clones' included), when the browser can list them. */
  private runningAnimations(): globalThis.Animation[] {
    const root = this.root as HTMLElement & { getAnimations?: (options?: { subtree?: boolean }) => globalThis.Animation[] };
    return typeof root.getAnimations === 'function' ? root.getAnimations({ subtree: true }) : [];
  }

  /** Computed values of the animated properties, or null when not rendered. */
  snapshot(el: HTMLElement): StyleSnapshot | null {
    if (!el.isConnected) return null;
    const cs = getComputedStyle(el);
    if (cs.display === 'none') return null;
    const snap: StyleSnapshot = {};
    for (const prop of ANIMATED_PROPERTIES) snap[prop] = cs[prop];
    return snap;
  }

  /** The animation root's rectangle in the viewport. */
  rootRect(): DOMRect {
    return this.root.getBoundingClientRect();
  }

  /**
   * Rectangle of `el` relative to the animation root (`base`: its rectangle,
   * if already read), or null when it is not drawn. A removed element's exit
   * clone is appended to the root, outside whatever hid the element: a
   * screen below the top one (`visibility: hidden`) or an ancestor at
   * opacity 0 must not leave a clone over the visible screen.
   */
  rectOf(el: HTMLElement, base: DOMRect = this.rootRect()): DOMRect | null {
    if (!el.isConnected || !isDrawn(el)) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return new DOMRect(r.left - base.left, r.top - base.top, r.width, r.height);
  }

  /**
   * Animate the pass. `commitAnimation` is the commit's own animation
   * (`withAnimation`); subtrees whose `animationToken` changed use their own
   * animation instead, as `.animation(_:value:)` overrides the transaction.
   * An `implicit` change (an inspector shown or hidden) animates even without
   * one, with the commit's animation or `default`. With `collect`, returns
   * the animations the commit started (a `withAnimation` completion waits
   * for them); else an empty array.
   */
  commit(pass: PassRecord, commitAnimation: Animation | undefined, frames: readonly FrameChange[] = [], collect = false): globalThis.Animation[] {
    const before = collect ? new Set(this.runningAnimations()) : null;
    this.animate(pass, commitAnimation, frames);
    return before ? this.runningAnimations().filter((animation) => !before.has(animation)) : [];
  }

  private animate(pass: PassRecord, commitAnimation: Animation | undefined, frames: readonly FrameChange[]): void {
    const tokenRoots = new Map<HTMLElement, Animation | undefined>();
    for (const [el, rec] of pass.updated) {
      if (rec.tokenChanged) tokenRoots.set(el, rec.implicit ? commitAnimation : rec.animation);
    }
    // iOS 26: a bar toolbar's groups change with a spring of their own (see
    // the top of the file); the bar's glass capsules, beside their toolbars
    // rather than around them, follow the screen's bar toolbars.
    // The spring moves the groups and their items, not what is inside an
    // item: a button whose label changes (a tool selected, its symbol now
    // on a tinted disc) changes in place, as UIKit redraws a bar item.
    const glassScreens = new Set<Element | null>();
    const groupsOnly = new Set<HTMLElement>();
    for (const [toolbar, screen] of implicitToolbars(pass, frames)) {
      if (!tokenRoots.has(toolbar)) {
        tokenRoots.set(toolbar, commitAnimation ?? TOOLBAR_ANIMATION);
        if (!commitAnimation) groupsOnly.add(toolbar);
      }
      glassScreens.add(screen);
    }
    if (!commitAnimation && tokenRoots.size === 0) {
      // Not animated: frames still tweening toward a box this pass replaced
      // carry on to the new box in the time they had left.
      for (const change of frames) this.retargetFrame(change, null);
      return;
    }

    // Matched geometry: a box inserted with the id of a removed one starts
    // from the removed one's frame, and the removed one's exit travels to
    // the new frame. Both frames are read now, before the tweens below hold
    // ancestors at their old geometry.
    const matchedPairs = this.matchedPairs(pass);
    const matchedTo = new Map<RemovedRecord, { from: DOMRect; to: DOMRect }>();

    // What a screen below the top of its stack holds is not drawn once the
    // push has settled (styles.css: `visibility: hidden`), so it is not
    // animated: it jumps to its new boxes, as the rows of a list a pushed
    // screen covers do when a split view's sidebar slides. During a push or
    // pop the lower screen shows, sliding, so then it animates as usual.
    const hiddenScreens = new Map<HTMLElement, boolean>();
    const hidden = (el: HTMLElement): boolean => {
      for (let screen = el.closest<HTMLElement>(BELOW_SCREEN); screen; screen = screen.parentElement?.closest<HTMLElement>(BELOW_SCREEN) ?? null) {
        let settled = hiddenScreens.get(screen);
        if (settled === undefined) {
          const stack = screen.closest<HTMLElement>('.sb-navstack');
          settled = stack !== null && stack.querySelector(NAV_IN_FLIGHT) === null;
          hiddenScreens.set(screen, settled);
        }
        if (settled) return true;
      }
      return false;
    };

    // The animation of the token root `el`, found `depth` levels up.
    const rootAnimation = (el: HTMLElement, depth: number): Animation | undefined | false => {
      // (below an item of a group the bar's own spring moves: not animated)
      if (groupsOnly.has(el) && depth > 1) return false;
      return tokenRoots.get(el) ?? { type: 'default' };
    };
    const animationFor = (chain: Iterable<HTMLElement>): Animation | undefined | false => {
      let depth = 0;
      for (const el of chain) {
        if (tokenRoots.has(el)) return rootAnimation(el, depth);
        depth++;
      }
      return commitAnimation ?? false;
    };
    // The same for an element in the page, up its ancestors (no chain built).
    const animationAt = (el: HTMLElement): Animation | undefined | false => {
      let depth = 0;
      for (let node: HTMLElement | null = el; node; node = node.parentElement) {
        if (tokenRoots.has(node)) return rootAnimation(node, depth);
        depth++;
      }
      return commitAnimation ?? false;
    };

    // Style changes.
    for (const [el, rec] of pass.updated) {
      if (!rec.before || !el.isConnected || hidden(el)) continue;
      const animation = animationAt(el);
      if (animation === false) continue;
      this.animateChanges(el, rec.before, animation);
    }

    // What elements animate themselves (a custom shape's path).
    if (this.playOwnFrames) {
      for (const el of pass.updated.keys()) {
        if (!el.isConnected || hidden(el)) continue;
        const animation = animationAt(el);
        if (animation !== false) this.playOwnFrames(el, animation);
      }
    }

    // Frame changes: tween left/top/width/height from the previous box. The
    // inline values are relative to the parent's origin, so apply the delta.
    const tweens: { change: FrameChange; from: FrameRect; animation: Animation | undefined; moved: boolean }[] = [];
    const jumped = new Map<HTMLElement, { x: number; y: number }>();
    for (const change of frames) {
      const { el, from, to } = change;
      if (!from || !el.isConnected || pass.inserted.has(el)) continue;
      if (this.selfAnimating.has(change.kind)) continue;
      if (hidden(el)) {
        // Not drawn: a tween still running on it (from a pass before the
        // push settled) carries on to the new box; otherwise it jumps.
        this.retargetFrame(change, null);
        continue;
      }
      let animation = animationAt(el);
      if (animation === false && change.kind === BAR_GLASS_KIND && glassScreens.has(el.parentElement)) {
        animation = TOOLBAR_ANIMATION;
      }
      // An item of such a group that only resized about its own center (its
      // label changed, its slot stayed) is redrawn in place, not tweened.
      if (animation !== false && groupsOnly.has(el.parentElement as HTMLElement) && sameCenter(from, to)) animation = false;
      if (animation === false) {
        // Moved in place, unless it carries on with a tween of its own.
        if (!this.retargetFrame(change, null)) jumped.set(el, { x: to.x - from.x, y: to.y - from.y });
        continue;
      }
      tweens.push({ change, from, animation, moved: true });
    }
    // The root of an `.animation(_:value:)` subtree whose own box did not
    // change still moves with an ancestor that jumped.
    const changed = new Set(frames.map((change) => change.el));
    for (const [el, animation] of tokenRoots) {
      if (changed.has(el) || !el.isConnected || pass.inserted.has(el) || hidden(el)) continue;
      // (the inline frame the layout applied: no layout read mid-commit)
      const box = { x: parseFloat(el.style.left) || 0, y: parseFloat(el.style.top) || 0, width: parseFloat(el.style.width) || 0, height: parseFloat(el.style.height) || 0 };
      tweens.push({ change: { el, kind: el.dataset['sbKind'] ?? '', from: box, to: box }, from: box, animation: animation ?? { type: 'default' }, moved: false });
    }
    // An animated element under an ancestor that jumps (outside the animated
    // subtree) starts from where it was drawn, not from its old place in the
    // moved ancestor: SwiftUI animates its position on screen, so a shape
    // that grows in a stack that re-centers grows about its center.
    const tweened = new Set(tweens.map((tween) => tween.change.el));
    for (const { change, from, animation, moved } of tweens) {
      const shift = jumpAbove(change.el, jumped, tweened, this.root);
      if (!moved && !shift) continue;
      const start = shift ? { ...from, x: from.x - shift.x, y: from.y - shift.y } : from;
      if (this.retargetFrame({ ...change, from: start }, animation, shift)) continue;
      this.animateFrame(change.el, start, change.to, animation);
    }

    // Matched geometry. The inserted box starts from the removed one's frame
    // relative to where it is drawn now: its ancestors' tweens have started
    // and carry it along, so the two together move it on screen. The removed
    // box's exit clone sits at the root and travels to the final frame, read
    // before any tween began.
    if (matchedPairs.length > 0) {
      const base = this.rootRect();
      for (const pair of matchedPairs) {
        const animation = animationFor([pair.box, ...ancestorsOf(pair.box)]);
        if (animation === false) continue;
        const drawn = this.rectOf(pair.box, base) ?? pair.to;
        this.animateMatched(pair.box, pair.from, drawn, animation);
        if (pair.rec.rect) matchedTo.set(pair.rec, { from: pair.from, to: pair.to });
      }
    }

    // Inserted elements: only subtree roots (a nested fade would compound).
    for (const el of pass.inserted) {
      if (!el.isConnected || pass.updated.has(el)) continue;
      if (hasAncestorIn(el, pass.inserted) || hidden(el)) continue;
      if (this.selfAnimating.has(el.dataset['sbKind'] ?? '')) continue;
      const animation = animationAt(el);
      if (animation === false) continue;
      this.enter(el, transitionOf(el) ?? implicitTransitionOf(el), animation);
    }

    // Content transitions: the old text leaves as the new one arrives.
    for (const change of pass.contentChanges) {
      if (!change.el.isConnected || hidden(change.el)) continue;
      const animation = animationAt(change.el);
      if (animation === false) continue;
      this.transitionContent(change, animation);
    }

    // Removed elements: leave a clone that plays the transition backwards.
    for (const rec of pass.removed) {
      if (!rec.rect) continue;
      if (this.selfAnimating.has(rec.el.dataset['sbKind'] ?? '')) continue;
      const animation = animationFor([rec.el, ...rec.ancestors]);
      if (animation === false) continue;
      const clone = this.exit(rec, animation);
      const move = matchedTo.get(rec);
      if (clone && move) this.moveClone(clone, move.from, move.to, animation);
    }
  }

  /** The matched boxes this pass inserted, with the frame of the removed box of the same id and their own. */
  private matchedPairs(pass: PassRecord): { box: HTMLElement; rec: RemovedRecord; from: DOMRect; to: DOMRect }[] {
    const removed = new Map<string, { rec: RemovedRecord; rect: DOMRect }>();
    for (const rec of pass.removed) {
      for (const m of rec.matched ?? []) removed.set(m.id, { rec, rect: m.rect });
    }
    if (removed.size === 0) return [];
    const pairs: { box: HTMLElement; rec: RemovedRecord; from: DOMRect; to: DOMRect }[] = [];
    const base = this.rootRect();
    for (const el of pass.inserted) {
      if (!el.isConnected || hasAncestorIn(el, pass.inserted)) continue;
      const boxes = el.dataset['sbMatched'] !== undefined ? [el] : [];
      boxes.push(...el.querySelectorAll<HTMLElement>('[data-sb-matched]'));
      for (const box of boxes) {
        const source = removed.get(box.dataset['sbMatched']!);
        const to = source ? this.rectOf(box, base) : null;
        if (source && to) pairs.push({ box, rec: source.rec, from: source.rect, to });
      }
    }
    return pairs;
  }

  /**
   * `.contentTransition`: a copy of the old text at its old place leaves
   * (upward for `numericText`, downward when counting down) while the new
   * text comes in from the other side; `opacity` crossfades in place.
   */
  transitionContent(change: ContentChange, animation: Animation | undefined): void {
    const clone = change.clone;
    clone.classList.add(EXIT_CLONE_CLASS);
    clone.setAttribute('aria-hidden', 'true');
    clone.toggleAttribute('inert', true);
    this.placeClone(clone, change.rect, change.el);
    const timing = baseTiming(timingFor(animation));
    const rolls = change.kind === 'numericText';
    const away = change.countsDown ? '0 45%' : '0 -45%';
    const toward = change.countsDown ? '0 -45%' : '0 45%';
    const out = clone.animate(
      [
        { opacity: 1, translate: '0 0', filter: 'blur(0px)' },
        { opacity: 0, translate: rolls ? away : '0 0', filter: rolls ? 'blur(2px)' : 'blur(0px)' },
      ],
      { ...timing, fill: 'forwards' },
    );
    change.el.animate(
      [
        { opacity: 0, translate: rolls ? toward : '0 0', filter: rolls ? 'blur(2px)' : 'blur(0px)' },
        { opacity: 1, translate: '0 0', filter: 'blur(0px)' },
      ],
      { ...timing, fill: 'backwards' },
    );
    const done = (): void => clone.remove();
    out.finished.then(done, done);
    window.setTimeout(done, timing.duration + timing.delay + 100);
  }

  /** FLIP a matched box from the removed one's frame to its own. */
  animateMatched(el: HTMLElement, from: DOMRect, to: DOMRect, animation: Animation | undefined): void {
    const sx = to.width > 0 ? from.width / to.width : 1;
    const sy = to.height > 0 ? from.height / to.height : 1;
    const own = el.style.transform;
    const start = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${sx}, ${sy})`;
    const timing = baseTiming(timingFor(animation));
    el.animate(
      [
        { transformOrigin: '0 0', transform: own ? `${start} ${own}` : start },
        { transformOrigin: '0 0', transform: own || 'none' },
      ],
      { ...timing, fill: 'backwards' },
    );
  }

  /** Move a removed box's exit clone so its matched box ends on the new frame. */
  private moveClone(clone: HTMLElement, from: DOMRect, to: DOMRect, animation: Animation | undefined): void {
    const sx = from.width > 0 ? to.width / from.width : 1;
    const sy = from.height > 0 ? to.height / from.height : 1;
    const timing = baseTiming(timingFor(animation));
    const shift = CLONE_SHIFT.get(clone) ?? { x: 0, y: 0 };
    clone.style.transformOrigin = `${from.left - shift.x - parseFloat(clone.style.left || '0')}px ${from.top - shift.y - parseFloat(clone.style.top || '0')}px`;
    clone.animate(
      [{ transform: 'none' }, { transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${sx}, ${sy})` }],
      { ...timing, fill: 'forwards', composite: 'add' },
    );
  }

  /** Tween every animated property whose computed value changed. */
  animateChanges(el: HTMLElement, before: StyleSnapshot, animation: Animation | undefined): void {
    const after = this.snapshot(el);
    if (!after) return;
    const from: Keyframe = {};
    const to: Keyframe = {};
    let changed = false;
    for (const prop of ANIMATED_PROPERTIES) {
      const a = before[prop];
      const b = after[prop];
      if (a === undefined || b === undefined || a === b) continue;
      from[prop] = a;
      to[prop] = b;
      changed = true;
    }
    if (!changed) return;
    const timing = timingFor(animation);
    el.animate([from, to], { ...timing, fill: 'none' });
  }

  /** Tween an element's box from its previous layout frame to the current one. */
  animateFrame(el: HTMLElement, from: FrameRect, to: FrameRect, animation: Animation | undefined): void {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dw = to.width - from.width;
    const dh = to.height - from.height;
    if (!dx && !dy && !dw && !dh) return;
    const left = parseFloat(el.style.left) || 0;
    const top = parseFloat(el.style.top) || 0;
    const start: Keyframe = {};
    const end: Keyframe = {};
    if (dx) {
      start['left'] = `${left - dx}px`;
      end['left'] = `${left}px`;
    }
    if (dy) {
      start['top'] = `${top - dy}px`;
      end['top'] = `${top}px`;
    }
    if (dw) {
      start['width'] = `${from.width}px`;
      end['width'] = `${to.width}px`;
    }
    if (dh) {
      start['height'] = `${from.height}px`;
      end['height'] = `${to.height}px`;
    }
    const timing = timingFor(animation);
    this.trackFrame(el, el.animate([start, end], { ...timing, fill: 'none' }));
  }

  /** The frame tween running on each element (see `retargetFrame`). */
  private readonly frameTweens = new WeakMap<HTMLElement, globalThis.Animation>();

  private trackFrame(el: HTMLElement, tween: globalThis.Animation): void {
    this.frameTweens.set(el, tween);
    tween.addEventListener('finish', () => {
      if (this.frameTweens.get(el) === tween) this.frameTweens.delete(el);
    });
    tween.addEventListener('cancel', () => {
      if (this.frameTweens.get(el) === tween) this.frameTweens.delete(el);
    });
  }

  /**
   * When `change.el` is still tweening toward the box this pass replaced,
   * replace that tween with one from where the element is drawn now to its
   * new inline frame: in `animation`'s timing when the pass animates the
   * element, else (`null`) in the time the old tween had left, with its
   * easing. Returns whether there was a tween to retarget.
   */
  retargetFrame(change: FrameChange, animation: Animation | undefined | null, shift: { x: number; y: number } | null = null): boolean {
    const { el, from, to } = change;
    const tween = this.frameTweens.get(el);
    if (!tween || tween.playState === 'finished' || tween.playState === 'idle') return false;
    if (!el.isConnected) {
      tween.cancel();
      return false;
    }
    // The drawn values: the tween's own properties from the computed style
    // (it is still applied), the others from the box before this pass.
    const tweened = new Set<string>();
    for (const frame of (tween.effect as KeyframeEffect | null)?.getKeyframes() ?? []) {
      for (const key of ['left', 'top', 'width', 'height']) if (key in frame) tweened.add(key);
    }
    const cs = getComputedStyle(el);
    const left = parseFloat(el.style.left) || 0;
    const top = parseFloat(el.style.top) || 0;
    const prev = from ?? to;
    const drawn: Record<'left' | 'top' | 'width' | 'height', number> = {
      left: tweened.has('left') ? parseFloat(cs.left) - (shift?.x ?? 0) : left - (to.x - prev.x),
      top: tweened.has('top') ? parseFloat(cs.top) - (shift?.y ?? 0) : top - (to.y - prev.y),
      width: tweened.has('width') ? parseFloat(cs.width) : prev.width,
      height: tweened.has('height') ? parseFloat(cs.height) : prev.height,
    };
    const target = { left, top, width: to.width, height: to.height };
    let timing: KeyframeAnimationOptions;
    if (animation === null) {
      const computed = tween.effect?.getComputedTiming();
      const end = Number(computed?.endTime ?? 0);
      const now = Number(tween.currentTime ?? 0);
      const remaining = Math.max(0, end - now);
      const easing = tween.effect?.getTiming().easing ?? 'linear';
      timing = { duration: remaining, easing };
    } else {
      timing = timingFor(animation);
    }
    tween.cancel();
    const start: Keyframe = {};
    const finish: Keyframe = {};
    for (const key of ['left', 'top', 'width', 'height'] as const) {
      if (!Number.isFinite(drawn[key]) || Math.abs(drawn[key] - target[key]) < 0.01) continue;
      start[key] = `${drawn[key]}px`;
      finish[key] = `${target[key]}px`;
    }
    if (Object.keys(start).length === 0 || !timing.duration) return true;
    this.trackFrame(el, el.animate([start, finish], { ...timing, fill: 'none' }));
    return true;
  }

  /** Play `transition` forwards on a freshly inserted element. */
  enter(el: HTMLElement, transition: Transition | undefined, animation: Animation | undefined): void {
    const start = transitionStart(transition, false);
    if (isIdentity(start)) return;
    const from: Keyframe = {};
    const to: Keyframe = {};
    if (start.opacity !== undefined) {
      from['opacity'] = start.opacity;
      to['opacity'] = 1;
    }
    if (start.transform !== undefined) {
      // A box with its own transform (offset, rotation, scale) keeps it: the
      // transition's transform is applied on top of it.
      const own = el.style.transform;
      from['transform'] = own ? `${own} ${start.transform}` : start.transform;
      to['transform'] = own || 'none';
    }
    const timing = baseTiming(timingFor(animation));
    el.animate([from, to], { ...timing, fill: 'backwards' });
  }

  /** The exit clones playing, each with the element it left (its parent at removal): `dropExitClonesIn`. */
  private readonly exitClones = new Map<HTMLElement, HTMLElement>();

  /**
   * Drops the exit clones of elements that left `container` or anything in
   * it: a clone stands in for content of a container that is now going
   * itself, and would play on over whatever takes the container's place. A
   * screen that swaps its content for a placeholder a commit before it is
   * popped (a split view's detail as its selection clears) left its bar
   * group's clone fading over the uncovered screen's bar. (A clone not built
   * yet, its pass still waiting for the frame, is refused in `exit`.)
   */
  dropExitClonesIn(container: HTMLElement): void {
    for (const [clone, owner] of this.exitClones) {
      if (owner !== container && !container.contains(owner)) continue;
      this.exitClones.delete(clone);
      for (const animation of clone.getAnimations()) animation.cancel();
      clone.remove();
    }
  }

  /**
   * Put a clone at `rect` (relative to the root) in the layer `from` is in:
   * the root, or the sheet it is in (a full-screen cover's slides), which is
   * drawn over the presenting tree; at the root, a clone of something in a
   * sheet would play under the sheet, unseen.
   */
  private placeClone(clone: HTMLElement, rect: DOMRect, from: HTMLElement | null): void {
    const sheet = from?.closest<HTMLElement>('.sb-sheet');
    const layer = sheet && this.root.contains(sheet) ? sheet : this.root;
    let shift = { x: 0, y: 0 };
    if (layer !== this.root) {
      const base = this.rootRect();
      const box = layer.getBoundingClientRect();
      shift = { x: box.left - base.left, y: box.top - base.top };
      CLONE_SHIFT.set(clone, shift);
    }
    const s = clone.style;
    s.position = 'absolute';
    s.left = `${rect.left - shift.x}px`;
    s.top = `${rect.top - shift.y}px`;
    s.width = `${rect.width}px`;
    s.height = `${rect.height}px`;
    s.margin = '0';
    s.pointerEvents = 'none';
    layer.append(clone);
  }

  /**
   * Re-insert a visual clone of a removed element at its old position and
   * play its transition backwards; the clone is dropped when done.
   */
  exit(rec: RemovedRecord, animation: Animation | undefined): HTMLElement | null {
    const parent = rec.ancestors[0] ?? null;
    // The element it left has gone itself since (a later pass in the same
    // task popped the screen): nothing is left to leave from.
    if (parent && !parent.isConnected) return null;
    const end = transitionStart(rec.transition ?? implicitTransitionOf(rec.el, parent), true);
    if (isIdentity(end) || !rec.rect) return null;
    const clone = ghostClone(rec.el);
    clone.classList.add(EXIT_CLONE_CLASS);
    // A bar item's clone leaves its toolbar: the stylesheet draws it as the
    // toolbar did (the symbol's size and weight, the label color).
    if (rec.el !== parent && isBarToolbar(parent)) clone.dataset['sbBarItem'] = parent.dataset['sbPlacement'] ?? '';
    clone.setAttribute('aria-hidden', 'true');
    clone.toggleAttribute('inert', true);
    this.placeClone(clone, rec.rect, parent);

    const from: Keyframe = {};
    const to: Keyframe = {};
    if (end.opacity !== undefined) {
      from['opacity'] = getComputedStyle(clone).opacity;
      to['opacity'] = end.opacity;
    }
    if (end.transform !== undefined) {
      const own = clone.style.transform;
      from['transform'] = own || 'none';
      to['transform'] = own ? `${own} ${end.transform}` : end.transform;
    }
    const timing = baseTiming(timingFor(animation));
    const anim = clone.animate([from, to], { ...timing, fill: 'forwards' });
    if (parent) this.exitClones.set(clone, parent);
    const done = (): void => {
      this.exitClones.delete(clone);
      clone.remove();
    };
    anim.finished.then(done, done);
    window.setTimeout(done, timing.duration + timing.delay + 100);
    return clone;
  }
}

/** Whether `el` is drawn: displayed, not `visibility: hidden`, no ancestor at opacity 0. */
function isDrawn(el: HTMLElement): boolean {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true, opacityProperty: true });
  }
  if (getComputedStyle(el).visibility === 'hidden') return false;
  for (let e: HTMLElement | null = el; e; e = e.parentElement) {
    if (getComputedStyle(e).opacity === '0') return false;
  }
  return true;
}

/** How far an exit clone's layer (a sheet) is from the animation root, when it is not the root: `placeClone`. */
const CLONE_SHIFT = new WeakMap<HTMLElement, { x: number; y: number }>();

/** Transitions are stored by the renderer per element (see `setTransition`). */
const TRANSITIONS = new WeakMap<HTMLElement, Transition>();

export function setTransition(el: HTMLElement, transition: Transition | undefined): void {
  if (transition) TRANSITIONS.set(el, transition);
  else TRANSITIONS.delete(el);
}

/**
 * The transition of a subtree root: its own, or the one on a single-child
 * chain of `styled` boxes below it (`Text.transition(.opacity).padding()`
 * is `styled{padding} > styled{transition} > text`), else the default.
 */
export function transitionOf(root: HTMLElement): Transition | undefined {
  let el: HTMLElement | null = root;
  while (el) {
    const own = TRANSITIONS.get(el);
    if (own) return own;
    if (!el.classList.contains('sb-styled')) break;
    const next: Element | null = el.firstElementChild;
    el = next instanceof HTMLElement && el.children.length === 1 ? next : null;
  }
  return undefined;
}

function hasAncestorIn(el: HTMLElement, set: ReadonlySet<HTMLElement>): boolean {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (set.has(node)) return true;
  }
  return false;
}

/**
 * How far the ancestors of `el` that jumped in this pass (moved without a
 * tween) moved it, up to the nearest ancestor that tweens (which carries `el`
 * from where it was drawn) or `root`; null when nothing above it jumped.
 */
function jumpAbove(
  el: HTMLElement,
  jumped: ReadonlyMap<HTMLElement, { x: number; y: number }>,
  tweened: ReadonlySet<HTMLElement>,
  root: HTMLElement,
): { x: number; y: number } | null {
  let x = 0;
  let y = 0;
  for (let node = el.parentElement; node && node !== root && !tweened.has(node); node = node.parentElement) {
    const jump = jumped.get(node);
    if (!jump) continue;
    x += jump.x;
    y += jump.y;
  }
  return x || y ? { x, y } : null;
}

/** The element's ancestors, nearest first. */
/** Whether two boxes share their center (to within a point: each edge is snapped to a whole point). */
function sameCenter(a: FrameRect, b: FrameRect): boolean {
  return Math.abs(a.x + a.width / 2 - (b.x + b.width / 2)) <= 1 && Math.abs(a.y + a.height / 2 - (b.y + b.height / 2)) <= 1;
}

function ancestorsOf(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (let a = el.parentElement; a; a = a.parentElement) out.push(a);
  return out;
}
